import { createSelector, createSlice } from '@reduxjs/toolkit'

import type { RootState } from '../store'
import { getCustomService } from '../../helpers/ReduxHelpers'
import {
  Declaration,
  DeclarationFunction,
  DeclarationFunctionPaginator,
  FamilyRelationship,
  QueryFunctionsArgs,
  QueryValidateSirenArgs,
  QueryValidateSirenOrRnaArgs,
  User,
} from '../../graphql/generated/graphql'
import { FormValues, FullDeclaration, NUMBER_OF_CHILDREN } from '../../types'

import { actionTypes } from './types'

//
// Services
//
export const functionsService = getCustomService<
  'functions',
  DeclarationFunctionPaginator,
  QueryFunctionsArgs
>('functions')

export const familyRelationshipsService = getCustomService<
  'familyRelationships',
  FamilyRelationship[],
  Record<string, never>
>('familyRelationships')

export const saveDeclarationService = getCustomService<'saveDeclaration', Declaration>(
  'saveDeclaration'
)

export const acceptCguService = getCustomService<'acceptCgu', User>('acceptCgu')

export const validateSirenService = getCustomService<
  'validateSiren',
  {
    address?: string | null
    city?: string | null
    postalCode?: string | null
    companyName?: string | null
  },
  QueryValidateSirenArgs
>('validateSiren')

export const validateSirenOrRnaService = getCustomService<
  'validateSirenOrRna',
  {
    address?: string | null
    city?: string | null
    postalCode?: string | null
    companyName?: string | null
  },
  QueryValidateSirenOrRnaArgs
>('validateSirenOrRna')

//
// Initial state
//

export type DeclarationState = {
  step: number
  previousSteps: Array<number>
  isUpdatingDeclaration: boolean
  formValues: FormValues
  declarationFunctions: DeclarationFunction[]
  familyRelationshipsList: FamilyRelationship[]
  isInDeclarationProcess: boolean

  // Service
  saveDeclaration: typeof saveDeclarationService.state
  acceptCgu: typeof acceptCguService.state
  validateSiren: typeof validateSirenService.state
  validateSirenOrRna: typeof validateSirenOrRnaService.state
}

const initialState: DeclarationState = {
  step: 0,
  previousSteps: [],
  isUpdatingDeclaration: false,
  isInDeclarationProcess: false,
  formValues: {
    terms: false,
    cciFunction: '',
    intent: '',
    items: [],
    partner: {
      civility: '',
      firstName: '',
      lastName: '',
      items: [],
    },
    children: [],
  },
  declarationFunctions: [],
  familyRelationshipsList: [],

  // Service
  saveDeclaration: saveDeclarationService.state,
  acceptCgu: acceptCguService.state,
  validateSiren: validateSirenService.state,
  validateSirenOrRna: validateSirenService.state,
}

//
// Slice (Actions & Reducers)
//

const slice = createSlice({
  name: 'declaration',
  initialState,
  reducers: {
    init: () => undefined,
    //
    // Form
    //
    reset: () => initialState,
    initFormValues: () => undefined,
    resetFormValues: (state) => {
      console.log('reset form values')
      state.formValues = initialState.formValues
    },
    setStep: (state, action: actionTypes.setStep) => {
      state.previousSteps = [...state.previousSteps, state.step]
      state.step = action.payload
    },
    setFormValues: (state, action: actionTypes.setFormValues) => {
      state.formValues = {
        ...state.formValues,
        [action.payload.name]: action.payload.value,
      }
    },
    setPreviousStep: (state) => {
      const previousStep = state.previousSteps.pop()
      state.step = previousStep || 0
    },
    resetPreviousSteps: (state) => {
      state.previousSteps = initialState.previousSteps
    },
    setNextStep: (state) => {
      state.previousSteps = [...state.previousSteps, state.step]

      if (state.step > 1) {
        state.isInDeclarationProcess = true
      }

      if (state.step > 8 && state.step >= (state.formValues.children?.length || 0) + 8) {
        state.step = 8 + NUMBER_OF_CHILDREN + 1
      } else {
        state.step = state.step + 1
      }
    },
    setDeclarationFunctions: (state, action: actionTypes.setDeclarationFunctions) => {
      state.declarationFunctions = action.payload
    },
    setFamilyRelationships: (state, action: actionTypes.setFamilyRelationships) => {
      state.familyRelationshipsList = action.payload
    },
    setIsUpdatingDeclaration: (state, action: actionTypes.setIsUpdatingDeclaration) => {
      state.isUpdatingDeclaration = action.payload
    },
    setIsInDeclarationProcess: (state, action: actionTypes.setIsInDeclarationProcess) => {
      state.isInDeclarationProcess = action.payload
    },
    //
    // Registrant
    //
    appendNewDeclarationItem: (state, action: actionTypes.appendNewDeclarationItem) => {
      ;(state.formValues?.items as FullDeclaration[])?.push(action.payload)
    },
    removeDeclarationItem: (state, action: actionTypes.removeDeclarationItem) => {
      ;(state.formValues?.items as FullDeclaration[])?.splice(action.payload, 1)
    },
    updateDeclarationItem: (state, action: actionTypes.updateDeclarationItem) => {
      ;(state.formValues?.items as FullDeclaration[])?.splice(
        action.payload.index,
        1,
        action.payload.item
      )
    },
    resetDeclarations: (state) => {
      state.formValues.items = initialState.formValues.items
    },
    //
    // Partner
    //
    setPartner: (state, action: actionTypes.setPartner) => {
      state.formValues.partner = {
        ...action.payload,
        items: state.formValues.partner?.items || [],
      }
    },
    resetPartner: (state) => {
      state.formValues.partner = initialState.formValues.partner
    },
    appendPartnerDeclarationItem: (state, action: actionTypes.appendPartnerDeclarationItem) => {
      ;(state.formValues?.partner?.items as FullDeclaration[])?.push(action.payload)
    },
    removePartnerDeclarationItem: (state, action: actionTypes.removePartnerDeclarationItem) => {
      ;(state.formValues?.partner?.items as FullDeclaration[])?.splice(action.payload, 1)
    },
    updatePartnerDeclarationItem: (state, action: actionTypes.updatePartnerDeclarationItem) => {
      ;(state.formValues?.partner?.items as FullDeclaration[])?.splice(
        action.payload.index,
        1,
        action.payload.item
      )
    },
    //
    // Children
    //
    setChild: (state, action: actionTypes.setChild) => {
      state.formValues.children = state.formValues.children || []
      state.formValues.children[action.payload.index] = {
        items: state.formValues.children[action.payload.index]?.items || [],
        ...action.payload.child,
      }
    },
    resetChildren: (state) => {
      state.formValues.children = initialState.formValues.children
    },
    removeChild: (state, action: actionTypes.removeChild) => {
      state.formValues.children?.splice(action.payload, 1)
    },
    appendChildDeclarationItem: (state, action: actionTypes.appendChildDeclarationItem) => {
      ;(state.formValues?.children?.[action.payload.childIndex]?.items as FullDeclaration[])?.push(
        action.payload.item
      )
    },
    removeChildDeclarationItem: (state, action: actionTypes.removeChildDeclarationItem) => {
      ;(
        state.formValues?.children?.[action.payload.childIndex]?.items as FullDeclaration[]
      )?.splice(action.payload.itemIndex, 1)
    },
    updateChildDeclarationItem: (state, action: actionTypes.updateChildDeclarationItem) => {
      ;(
        state.formValues?.children?.[action.payload.childIndex]?.items as FullDeclaration[]
      )?.splice(action.payload.itemIndex, 1, action.payload.item)
    },
    //
    // Services
    //
    ...functionsService.reducers,
    ...familyRelationshipsService.reducers,
    ...saveDeclarationService.reducers,
    ...acceptCguService.reducers,
    ...validateSirenService.reducers,
    ...validateSirenOrRnaService.reducers,
  },
  // extraReducers: {
  //   [REHYDRATE]: (state, action: PayloadAction<any>) => {
  //     const { declaration = null } = action.payload?.declaration || {}

  //     return {
  //       ...initialState,
  //       declaration,
  //     }
  //   },
  // },
})

export const { reducer, actions } = slice

//
// Selectors
//

const root = (state: RootState) => state[slice.name]
const step = (state: RootState) => root(state).step
const previousSteps = (state: RootState) => root(state).previousSteps
const hasPreviousStep = createSelector([previousSteps], (steps): boolean => {
  return !!(steps.length || (steps.length === 1 && steps[0] !== 0))
})
const formValues = (state: RootState) => root(state).formValues
const declarationFunctions = (state: RootState) => root(state).declarationFunctions
const familyRelationships = (state: RootState) => root(state).familyRelationshipsList
const saveDeclaration = (state: RootState) => root(state).saveDeclaration
const acceptCgu = (state: RootState) => root(state).acceptCgu
const validateSiren = (state: RootState) => root(state).validateSiren
const validateSirenOrRna = (state: RootState) => root(state).validateSirenOrRna
const isUpdatingDeclaration = (state: RootState) => root(state).isUpdatingDeclaration
const isInDeclarationProcess = (state: RootState) => root(state).isInDeclarationProcess

export const selectors = {
  step,
  formValues,
  declarationFunctions,
  familyRelationships,
  saveDeclaration,
  acceptCgu,
  validateSiren,
  validateSirenOrRna,
  isUpdatingDeclaration,
  isInDeclarationProcess,
  hasPreviousStep,
  previousSteps,
}
